interface DecodedToken {
  exp: number;
  iat: number;
  [key: string]: any;
}

/**
 * Decodes a JWT token
 * @param token - The JWT token to decode
 * @returns Decoded token payload or null if invalid
 */
export const decodeToken = (token: string): DecodedToken | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Checks if a JWT token is expired
 * @param token - The JWT token to check
 * @returns boolean - True if token is expired or invalid, false if valid
 */
export const isTokenExpired = (token: string): boolean => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  // Convert exp time to milliseconds
  const expTime = decoded.exp * 1000;
  const currentTime = Date.now();
  
  // Add a small buffer (5 seconds) to account for clock skew
  const bufferTime = 5000;
  
  return currentTime >= (expTime - bufferTime);
};

/**
 * Gets the remaining time in milliseconds until token expiration
 * @param token - The JWT token to check
 * @returns number - Remaining time in milliseconds, or 0 if expired/invalid
 */
export const getTokenRemainingTime = (token: string): number => {
  if (!token) return 0;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return 0;
  
  const expTime = decoded.exp * 1000;
  const currentTime = Date.now();
  
  return Math.max(0, expTime - currentTime);
};

/**
 * Checks if the user should refresh their token
 * @param token - The JWT token to check
 * @param thresholdSeconds - Optional: Number of seconds before expiration to consider for refresh (default: 300s / 5 minutes)
 * @returns boolean - True if token should be refreshed, false otherwise
 */
export const shouldRefreshToken = (token: string, thresholdSeconds: number = 300): boolean => {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const expTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const thresholdMs = thresholdSeconds * 1000;
  
  return (expTime - currentTime) <= thresholdMs;
};